import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { WorkspaceTokenScope } from '@/lib/api';
import { SCOPE_HELP } from '@/lib/workspaceTokens';

const selectClass =
  'flex h-9 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** Expiry presets in days; 0 means the token never expires. */
const EXPIRY_OPTIONS = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '1 year' },
  { days: 0, label: 'Never' },
] as const;

interface CreatePayload {
  name: string;
  scope: WorkspaceTokenScope;
  expiresAt?: string;
}

interface Props {
  open: boolean;
  pending: boolean;
  onClose: () => void;
  onCreate: (input: CreatePayload) => Promise<void> | void;
}

export function WorkspaceTokenDialog({ open, pending, onClose, onCreate }: Props) {
  const [name, setName] = useState('');
  const [scope, setScope] = useState<WorkspaceTokenScope>('read');
  const [expiryDays, setExpiryDays] = useState<number>(90);

  useEffect(() => {
    if (open) {
      setName('');
      setScope('read');
      setExpiryDays(90);
    }
  }, [open]);

  const canSubmit = name.trim().length > 0 && !pending;

  function submit() {
    if (!canSubmit) return;
    void onCreate({
      name: name.trim(),
      scope,
      expiresAt:
        expiryDays > 0
          ? new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000).toISOString()
          : undefined,
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Create API token"
      description="The token is shown once after creation. Store it in your application's secret manager."
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={pending} data-dialog-close>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {pending ? 'Creating…' : 'Create token'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="token-name">Name</Label>
          <Input
            id="token-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="sync-service"
            maxLength={100}
            autoFocus
            required
          />
          <p className="text-[11px] text-muted-foreground">
            What uses this token. Unique within this workspace.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="token-scope">Scope</Label>
          <select
            id="token-scope"
            value={scope}
            onChange={(e) => setScope(e.target.value as WorkspaceTokenScope)}
            className={selectClass}
            aria-describedby="token-scope-help"
          >
            <option value="read">read</option>
            <option value="write">write</option>
            <option value="admin">admin</option>
          </select>
          <p id="token-scope-help" className="text-[11px] text-muted-foreground">
            {SCOPE_HELP[scope]}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="token-expiry">Expires</Label>
          <select
            id="token-expiry"
            value={expiryDays}
            onChange={(e) => setExpiryDays(Number(e.target.value))}
            className={selectClass}
          >
            {EXPIRY_OPTIONS.map((o) => (
              <option key={o.days} value={o.days}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </form>
    </Dialog>
  );
}
